import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../../../shared/api/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserPermissions } from "../../../../../src/shared/lib/auth/authHelper";
import "./DealsPage.css";

const normalize = (v) =>
  v === null || v === undefined ? "" : String(v).toLowerCase();

const getDealStatusLabel = (status, t) => {
  switch (status) {
    case "won":
      return t("won", { defaultValue: "Won" });
    case "lost":
      return t("crm_deals_status_lost", { defaultValue: "Lost" });
    case "open":
      return t("open", { defaultValue: "Open" });
    default:
      return status || "—";
  }
};

const formatDealApiError = (error, fallback, t) => {
  const detail =
    error?.response?.data?.detail ?? error?.response?.data?.message;

  const formatItem = (item) => {
    if (typeof item === "string") return item;
    if (!item || typeof item !== "object") return "";

    const message =
      typeof item.msg === "string"
        ? item.msg
        : typeof item.message === "string"
          ? item.message
          : "";
    const field = Array.isArray(item.loc)
      ? item.loc
          .filter((part) => part !== "body")
          .map(String)
          .join(".")
      : "";

    return field && message ? `${field}: ${message}` : message;
  };

  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const messages = detail.map(formatItem).filter(Boolean);
    if (messages.length) return messages.join("; ");
  }

  if (detail && typeof detail === "object") {
    const message = formatItem(detail);
    if (message) return message;
  }

  if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
    return t("network_connection_failed");
  }

  return fallback;
};

const formatDateForFilter = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString().toLowerCase();
  } catch {
    return String(value).toLowerCase();
  }
};

const applySorting = (items, config) => {
  if (!config || !config.key) return items;

  const { key, direction } = config;
  const dir = direction === "asc" ? 1 : -1;

  const getVal = (deal) => {
    if (!deal) return null;

    switch (key) {
      case "ui_id":
        return deal.ui_id != null ? Number(deal.ui_id) : null;
      case "amount":
      case "prepayment":
      case "balance":
      case "company_id":
      case "contact_ui_id":
      case "owner_ui_id":
      case "stage_id":
        return deal[key] != null ? Number(deal[key]) : null;
      case "created_at":
        return deal[key] ? new Date(deal[key]).getTime() : null;
      default:
        return deal[key] ?? null;
    }
  };

  return [...items].sort((a, b) => {
    const av = getVal(a);
    const bv = getVal(b);

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
};

const defaultColumnFilters = {
  ui_id: "",
  title: "",
  amount: "",
  prepayment: "",
  balance: "",
  status: "",
  company_id: "",
  contact_ui_id: "",
  owner_ui_id: "",
  stage_id: "",
  created_at: "",
};


const getCompactPaginationItems = (currentPage, totalPages) => {
  const total = Number(totalPages || 0);
  const current = Math.min(Math.max(Number(currentPage || 1), 1), Math.max(total, 1));
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) items.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
};

const DealsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [deals, setDeals] = useState([]);
  const [stages, setStages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "ui_id",
    direction: "desc",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState(defaultColumnFilters);

  const [currentPage, setCurrentPage] = useState(1);
  const [dealsPerPage, setDealsPerPage] = useState(10);
  const [showCompletedDeals, setShowCompletedDeals] = useState(false);

  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState(null);
  const [savingFilter, setSavingFilter] = useState(false);

  const [openMenuDealId, setOpenMenuDealId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const openMenuRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const dealUiId = params.get("deal_ui_id") || params.get("ui_id");
    const dealId = params.get("deal_id");
    const search = params.get("search");

    if (dealUiId) {
      setColumnFilters((prev) => ({ ...prev, ui_id: String(dealUiId) }));
      setSearchQuery(String(dealUiId));
      setCurrentPage(1);
      return;
    }

    if (dealId) {
      setSearchQuery(String(dealId));
      setCurrentPage(1);
      return;
    }

    if (search) {
      setSearchQuery(String(search));
      setCurrentPage(1);
    }
  }, [location.search]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get("/deals", {
        params: { skip: 0, limit: 500 },
      });

      setDeals(res.data || []);
      setCurrentPage(1);
    } catch (e) {
      console.error(e);
      setError(formatDealApiError(e, t("crm_deals_failed_load_data"), t));
    } finally {
      setLoading(false);
    }
  };

  const fetchStages = async () => {
    try {
      const res = await api.get("/pipeline-stages");
      setStages(res.data || []);
    } catch (e) {
      console.error(e);
      setStages([]);
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await api.get("/tasks", { params: { skip: 0, limit: 1000 } });
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setTasks([]);
    }
  };

  const tasksByDealId = useMemo(() => {
    const map = new Map();
    for (const task of tasks) {
      if (!task?.deal_id) continue;
      const key = String(task.deal_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(task);
    }
    return map;
  }, [tasks]);

  const openTaskFromLink = (task) => {
    if (!task) return;
    navigate("/menu/sales/tasks", {
      state: {
        open_task_id: task.ui_id || task.id,
        task_search: `#task-${task.ui_id || task.id}`,
      },
    });
  };

  const renderTaskLinks = (deal) => {
    const list = tasksByDealId.get(String(deal.id)) || tasksByDealId.get(String(deal.ui_id)) || [];
    if (!list.length) return "-";

    const first = list[0];
    const more = list.length - 1;

    return (
      <div className="dealTaskLinks">
        <button type="button" className="dealTaskLink" onClick={() => openTaskFromLink(first)}>
          ✅ #task-{first.ui_id || first.id}
        </button>
        {more > 0 ? <span className="dealTaskMore">+{more}</span> : null}
      </div>
    );
  };

  const stageNameById = useMemo(() => {
    const map = {};
    for (const s of stages) map[s.id] = s.name;
    return map;
  }, [stages]);

  const loadSavedFilters = async () => {
    try {
      setFiltersLoading(true);
      setFiltersError(null);

      const res = await api.get("/saved-filters/deals");
      const items = res.data || [];
      setSavedFilters(items);

      const def = items.find((f) => f.is_default);
      if (def && !selectedFilterId) {
        applySavedFilter(def);
      }
    } catch (e) {
      console.error(e);
      setFiltersError(
        formatDealApiError(e, t("error_loading_saved_filters"), t),
      );
    } finally {
      setFiltersLoading(false);
    }
  };

  const applySavedFilter = (filter) => {
    if (!filter) return;

    const filtersObj = filter.filters || {};
    const cf = filtersObj.columnFilters || defaultColumnFilters;
    const sq = filtersObj.searchQuery || "";
    const showCompleted = Boolean(filtersObj.showCompletedDeals);

    setSearchQuery(sq);
    setShowCompletedDeals(showCompleted);
    setColumnFilters({
      ...defaultColumnFilters,
      ...cf,
    });

    const savedKey = filter.sort_by || "ui_id";

    setSortConfig({
      key: savedKey === "id" ? "ui_id" : savedKey,
      direction: filter.sort_dir || "desc",
    });

    setDealsPerPage(filter.per_page || 10);
    setCurrentPage(1);
    setSelectedFilterId(filter.id);
  };

  const handleSelectSavedFilter = (e) => {
    const val = e.target.value;

    if (!val) {
      setSelectedFilterId(null);
      return;
    }

    const id = Number(val);
    const f = savedFilters.find((x) => x.id === id);
    if (f) applySavedFilter(f);
  };

  const handleSaveCurrentAsNew = async () => {
    const name = window.prompt(t("enter_filter_name")) || "";
    if (!name.trim()) return;

    const makeDefault = window.confirm(t("make_filter_default"));

    const payload = {
      name: name.trim(),
      filters: {
        searchQuery,
        columnFilters,
        showCompletedDeals,
      },
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
      per_page: dealsPerPage,
      is_default: makeDefault,
    };

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.post("/saved-filters/deals", payload);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(formatDealApiError(e, t("error_saving_filter"), t));
    } finally {
      setSavingFilter(false);
    }
  };

  const handleUpdateSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(t("select_filter_first"));
      return;
    }

    const makeDefault = window.confirm(t("make_filter_default"));

    const payload = {
      name: undefined,
      filters: {
        searchQuery,
        columnFilters,
        showCompletedDeals,
      },
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
      per_page: dealsPerPage,
      is_default: makeDefault,
    };

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.put(`/saved-filters/${selectedFilterId}`, payload);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(formatDealApiError(e, t("error_updating_filter"), t));
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(t("select_filter_first"));
      return;
    }

    const confirmed = window.confirm(t("confirm_delete_filter"));
    if (!confirmed) return;

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.delete(`/saved-filters/${selectedFilterId}`);
      setSelectedFilterId(null);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(formatDealApiError(e, t("error_deleting_filter"), t));
    } finally {
      setSavingFilter(false);
    }
  };

  useEffect(() => {
    fetchDeals();
    fetchStages();
    fetchTasks();
    loadSavedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (openMenuDealId == null) return;

    const onDown = (e) => {
      if (!openMenuRef.current) return;
      if (!openMenuRef.current.contains(e.target)) {
        setOpenMenuDealId(null);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenuDealId(null);
    };

    const onScroll = () => {
      setOpenMenuDealId(null);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [openMenuDealId]);

  const openActionsMenu = (dealId, event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();

    const menuWidth = 240;
    const menuHeight = 226;

    const left = Math.max(
      8,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)
    );

    const top =
      rect.bottom + menuHeight > window.innerHeight
        ? Math.max(8, rect.top - menuHeight - 6)
        : rect.bottom + 6;

    setMenuPosition({ top, left });
    setOpenMenuDealId((prev) => (prev === dealId ? null : dealId));
  };

  const patchDealInState = (dealId, patch) => {
    setDeals((prev) =>
      prev.map((item) => (item.id === dealId ? { ...item, ...patch } : item))
    );
  };

  const handleUpdateDealAmount = async (deal, rawValue) => {
    if (!deal || updatingId !== null) return;

    const value = String(rawValue ?? "").trim();
    const previousAmount = deal.amount;
    const previousBalance = deal.balance;
    const previousPaymentStatus = deal.payment_status;
    const previousFinalAmount = deal.final_amount;

    let amount = null;

    if (value !== "") {
      const numeric = Number(value);

      if (!Number.isFinite(numeric) || numeric < 0) {
        setError(
          t("invalid_deal_amount", {
            defaultValue: "Budget must be a non-negative number",
          })
        );
        return;
      }

      amount = Number(numeric.toFixed(2));
    }

    const previousNormalized =
      previousAmount === null || previousAmount === undefined || previousAmount === ""
        ? null
        : Number(Number(previousAmount).toFixed(2));

    if (previousNormalized === amount) return;

    const prepayment = Number(deal.prepayment || 0);
    const balance =
      amount === null ? null : Math.max(Number((amount - prepayment).toFixed(2)), 0);
    const paymentStatus =
      amount === null || amount <= 0
        ? prepayment > 0
          ? "paid"
          : "unpaid"
        : prepayment <= 0
          ? "unpaid"
          : prepayment >= amount
            ? "paid"
            : "partial";

    patchDealInState(deal.id, {
      amount,
      balance,
      payment_status: paymentStatus,
    });

    setUpdatingId(deal.id);
    setError(null);

    try {
      const response = await api.put(`/deals/${deal.id}`, { amount });
      const updated = response?.data;

      if (updated) {
        patchDealInState(deal.id, updated);
      }
    } catch (e) {
      console.error(e);

      patchDealInState(deal.id, {
        amount: previousAmount,
        balance: previousBalance,
        payment_status: previousPaymentStatus,
        final_amount: previousFinalAmount,
      });

      setError(
        formatDealApiError(
          e,
          t("crm_deals_failed_update_amount", {
            defaultValue: "Failed to update deal budget",
          }),
          t,
        ),
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const renderAmountInput = (deal, compact = false) => (
    <input
      type="number"
      min="0"
      step="0.01"
      className={
        compact
          ? "dealsInlineAmount dealsInlineAmountMobile"
          : "dealsInlineAmount"
      }
      defaultValue={deal.amount ?? ""}
      disabled={updatingId === deal.id}
      placeholder={t("sales_deals_amount")}
      aria-label={t("sales_deals_amount")}
      onBlur={(event) =>
        handleUpdateDealAmount(deal, event.currentTarget.value)
      }
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.currentTarget.blur();
        }

        if (event.key === "Escape") {
          event.currentTarget.value = deal.amount ?? "";
          event.currentTarget.blur();
        }
      }}
    />
  );

  const requestCompletionSummary = (deal) => {
    const existingSummary = String(deal?.work_summary || "").trim();
    if (existingSummary.length >= 3) return existingSummary;

    const enteredSummary = window.prompt(
      t("crm_deals_completion_summary_prompt", {
        defaultValue:
          "Кратко опишите результат работы перед завершением сделки:",
      }),
      existingSummary,
    );
    if (enteredSummary === null) return null;

    const summary = enteredSummary.trim();
    if (summary.length < 3) {
      window.alert(
        t("work_summary_required", {
          defaultValue: "Укажите результат работы — минимум 3 символа.",
        }),
      );
      return null;
    }

    return summary;
  };

  const updateDealOnServer = async (deal, patch) => {
    const payload = {
      title: deal.title,
      amount: deal.amount,
      status: patch.status ?? deal.status,
      stage_id: Object.prototype.hasOwnProperty.call(patch, "stage_id")
        ? patch.stage_id
        : (deal.stage_id ?? null),
    };

    if (Object.prototype.hasOwnProperty.call(patch, "work_summary")) {
      payload.work_summary = patch.work_summary;
    }

    return api.put(`/deals/${deal.id}`, payload);
  };

  const updateDealStatus = async (deal, newStatus) => {
    if (updatingId !== null) return;
    if (deal.status === newStatus) return;

    const confirmed = window.confirm(
      t("confirm_change_deal_status", {
        id: deal.ui_id,
        title: deal.title,
        status: getDealStatusLabel(newStatus, t),
        defaultValue:
          'Change status of deal #{{id}} "{{title}}" to "{{status}}"?',
      })
    );

    if (!confirmed) return;

    const patch = { status: newStatus };
    if (newStatus === "won" || newStatus === "lost") {
      const workSummary = requestCompletionSummary(deal);
      if (!workSummary) return;
      patch.work_summary = workSummary;
    }

    try {
      setUpdatingId(deal.id);
      setError(null);

      const response = await updateDealOnServer(deal, patch);
      const updatedDeal = response?.data;

      setDeals((prev) =>
        prev.map((item) =>
          item.id === deal.id
            ? updatedDeal || {
                ...item,
                status: newStatus,
                work_summary: patch.work_summary ?? item.work_summary,
              }
            : item,
        ),
      );

      if (newStatus === "won" || newStatus === "lost") {
        setTasks((prev) =>
          prev.map((task) => {
            const belongsToDeal =
              String(task?.deal_id ?? "") === String(deal.id) ||
              (task?.deal_ui_id != null &&
                String(task.deal_ui_id) === String(deal.ui_id));

            return belongsToDeal && task.status !== "done"
              ? { ...task, status: "done" }
              : task;
          }),
        );
      }

      window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));
    } catch (e) {
      console.error(e);
      setError(
        formatDealApiError(e, t("crm_deals_failed_update_status"), t),
      );
    } finally {
      setUpdatingId(null);
    }
  };


  const applyCouponToDeal = async (deal) => {
    if (updatingId !== null) return;
    if (deal.coupon_code) {
      alert(t("deal_coupon_already_applied", { defaultValue: "Coupon is already applied to this deal" }));
      return;
    }

    const couponCode = window.prompt(
      t("deal_coupon_enter_code", { defaultValue: "Enter coupon code" })
    );
    if (!couponCode || !couponCode.trim()) return;

    try {
      setUpdatingId(deal.id);
      setError(null);
      setOpenMenuDealId(null);

      const res = await api.post(`/deals/${deal.id}/apply-coupon`, {
        coupon_code: couponCode.trim(),
      });

      const updated = res.data;
      setDeals((prev) => prev.map((d) => (d.id === deal.id ? updated : d)));
    } catch (e) {
      console.error(e);
      setError(
        formatDealApiError(
          e,
          t("deal_coupon_failed_apply", { defaultValue: "Failed to apply coupon" }),
          t,
        ),
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteDeal = async (deal) => {
    if (updatingId !== null) return;

    const confirmed = window.confirm(
      t("confirm_delete_deal", {
        defaultValue: "Delete deal #{{id}}?",
        id: deal.ui_id,
        title: deal.title,
      })
    );

    if (!confirmed) return;

    try {
      setUpdatingId(deal.id);
      setError(null);
      setOpenMenuDealId(null);

      await api.delete(`/deals/${deal.id}`);

      setDeals((prev) => prev.filter((d) => d.id !== deal.id));
    } catch (e) {
      console.error(e);
      setError(
        formatDealApiError(
          e,
          t("error_deleting_deal", {
            defaultValue: "Failed to delete deal",
          }),
          t,
        ),
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const isStatusActionDisabled = (deal, targetStatus) =>
    updatingId === deal.id || deal.status === targetStatus;

  const getStageLabel = (deal) => {
    if (deal.status === "won") return t("won");
    if (deal.status === "lost") return t("crm_deals_status_lost");

    return deal.stage_id
      ? stageNameById[deal.stage_id] || `#${deal.stage_id}`
      : "-";
  };

  const filteredAndSortedDeals = useMemo(() => {
    let result = [...deals];

    if (!showCompletedDeals) {
      result = result.filter((deal) => {
        const status = normalize(deal?.status);
        return status !== "won" && status !== "lost";
      });
    }

    const global = searchQuery.trim().toLowerCase();

    if (global) {
      result = result.filter((d) => {
        const fields = [
          d.id,
          d.ui_id,
          d.title,
          d.amount,
          d.prepayment,
          d.balance,
          d.payment_status,
          d.status,
          d.company_id,
          d.contact_ui_id,
          d.owner_ui_id,
          d.stage_id,
          stageNameById[d.stage_id] || "",
          formatDateForFilter(d.created_at),
        ];

        return fields.some((f) => normalize(f).includes(global));
      });
    }

    const cf = columnFilters;

    result = result.filter((d) => {
      if (cf.ui_id && !String(d.ui_id ?? "").includes(cf.ui_id.trim())) return false;

      if (cf.title && !normalize(d.title).includes(cf.title.trim().toLowerCase())) {
        return false;
      }

      if (cf.amount && !String(d.amount ?? "").includes(cf.amount.trim())) {
        return false;
      }

      if (cf.prepayment && !String(d.prepayment ?? "").includes(cf.prepayment.trim())) {
        return false;
      }

      if (cf.balance && !String(d.balance ?? "").includes(cf.balance.trim())) {
        return false;
      }

      if (cf.status && !normalize(d.status).includes(cf.status.trim().toLowerCase())) {
        return false;
      }

      if (cf.company_id && !String(d.company_id ?? "").includes(cf.company_id.trim())) {
        return false;
      }

      if (
        cf.contact_ui_id &&
        !String(d.contact_ui_id ?? "").includes(cf.contact_ui_id.trim())
      ) {
        return false;
      }

      if (
        cf.owner_ui_id &&
        !String(d.owner_ui_id ?? "").includes(cf.owner_ui_id.trim())
      ) {
        return false;
      }

      if (cf.stage_id) {
        const selectedId = Number(cf.stage_id);
        if (Number(d.stage_id ?? 0) !== selectedId) return false;
      }

      if (
        cf.created_at &&
        !formatDateForFilter(d.created_at).includes(cf.created_at.trim().toLowerCase())
      ) {
        return false;
      }

      return true;
    });

    return applySorting(result, sortConfig);
  }, [deals, searchQuery, columnFilters, sortConfig, stageNameById, showCompletedDeals]);

  const totalDeals = filteredAndSortedDeals.length;
  const totalPages = Math.max(1, Math.ceil(totalDeals / (dealsPerPage || 1)));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * dealsPerPage;

  const visibleDeals = filteredAndSortedDeals.slice(
    startIdx,
    startIdx + dealsPerPage
  );

  const paginate = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleDealsPerPageChange = (e) => {
    const value = Number(e.target.value);
    if (!value || value <= 0) return;

    setDealsPerPage(value);
    setCurrentPage(1);
  };

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      let direction = "asc";
      if (prev.key === key && prev.direction === "asc") direction = "desc";
      return { key, direction };
    });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const showingText =
    totalDeals === 0
      ? t("showing_entries", { start: 0, end: 0, total: 0 })
      : t("showing_entries", {
          start: startIdx + 1,
          end: Math.min(startIdx + dealsPerPage, totalDeals),
          total: totalDeals,
        });

  const handleRefreshClick = async () => {
    setSearchQuery("");
    setColumnFilters(defaultColumnFilters);
    setSortConfig({ key: "ui_id", direction: "desc" });
    setDealsPerPage(10);
    setShowCompletedDeals(false);
    setCurrentPage(1);
    setSelectedFilterId(null);
    setFiltersError(null);
    setOpenMenuDealId(null);

    await fetchDeals();
    await fetchStages();
  };

  const kebabBtnStyle = {
    width: 34,
    height: 24,
    padding: 0,
    fontSize: 16,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const menuStyle = {
    position: "fixed",
    top: menuPosition.top,
    left: menuPosition.left,
    minWidth: 240,
    zIndex: 99999,
    padding: 8,
    borderRadius: 12,
    background: "rgba(25, 25, 30, 0.98)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 18px 42px rgba(0,0,0,0.55)",
  };

  const itemStyle = (disabled) => ({
    width: "100%",
    textAlign: "left",
    padding: "9px 10px",
    borderRadius: 9,
    border: "none",
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.45 : 1,
  });

  const iconStyle = {
    width: 20,
    height: 20,
    borderRadius: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 20px",
  };


  const entityButtonStyle = {
    border: "none",
    background: "transparent",
    color: "#8fb3ff",
    padding: 0,
    fontWeight: 750,
    textAlign: "left",
    cursor: "pointer",
    lineHeight: 1.16,
    display: "block",
    maxWidth: 220,
    minWidth: 0,
  };

  const entityTitleStyle = {
    display: "block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const entityMetaStyle = {
    display: "block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "rgba(255,255,255,0.52)",
    fontSize: 11,
    marginTop: 2,
    fontWeight: 600,
  };

  const CompactEntityButton = ({ label, meta, title, onClick }) => {
    if (!label) return "-";
    return (
      <button type="button" style={entityButtonStyle} title={title || label} onClick={onClick}>
        <span style={entityTitleStyle}>{label}</span>
        {meta ? <span style={entityMetaStyle}>{meta}</span> : null}
      </button>
    );
  };

  const renderCompanyCell = (deal) => {
    if (!deal?.company_id) return "-";
    const label = deal.company_display_name || deal.company_name || `Company #${deal.company_ui_id || deal.company_id}`;
    const meta = deal.company_ui_id ? `Company #${deal.company_ui_id}` : "";
    return (
      <CompactEntityButton
        label={label}
        meta={meta}
        title={t("open_company", { defaultValue: "Open company" })}
        onClick={() => navigate(`/menu/companies?company_id=${deal.company_id}`)}
      />
    );
  };

  const renderContactCell = (deal) => {
    if (!deal?.contact_id) return "-";
    const label = deal.contact_display_name || deal.contact_primary_phone || deal.contact_primary_email || `Contact #${deal.contact_ui_id || deal.contact_id}`;
    const meta = deal.contact_primary_phone || deal.contact_primary_email || (deal.contact_ui_id ? `Contact #${deal.contact_ui_id}` : "");
    return (
      <CompactEntityButton
        label={label}
        meta={meta}
        title={t("open_contact", { defaultValue: "Open contact" })}
        onClick={() => navigate(`/menu/sales/contacts?contact_id=${deal.contact_id}`)}
      />
    );
  };

  const renderOwnerCell = (deal) => {
    if (!deal?.owner_id) return "-";
    const label = deal.owner_display_name || deal.owner_full_name || deal.owner_email || `Employee #${deal.owner_ui_id || deal.owner_id}`;
    const meta = deal.owner_email || (deal.owner_ui_id ? `Employee #${deal.owner_ui_id}` : "");
    return (
      <CompactEntityButton
        label={label}
        meta={meta}
        title={t("open_employee", { defaultValue: "Open employee" })}
        onClick={() => navigate(`/menu/employees?user_id=${deal.owner_id}`)}
      />
    );
  };



  const MobileRelationLine = ({ label, children }) => (
    <span className="crmMobileRelationLine">
      <span className="crmMobileRelationLabel">{label}</span>
      <span className="crmMobileRelationValue">{children}</span>
    </span>
  );

  const MenuItem = ({
    title,
    disabled,
    icon,
    iconBg,
    iconBorder,
    onClick,
    children,
  }) => {
    return (
      <div title={title}>
        <button
          type="button"
          style={itemStyle(disabled)}
          aria-disabled={disabled ? "true" : "false"}
          onClick={() => {
            if (disabled) return;
            onClick?.();
          }}
        >
          <span
            style={{
              ...iconStyle,
              background: iconBg,
              border: iconBorder,
            }}
            aria-hidden="true"
          >
            {icon}
          </span>
          <span style={{ flex: 1 }}>{children}</span>
        </button>
      </div>
    );
  };

  return (
    <div className="container mt-4 dealsPage">
      <div data-academy="deals-header" className="dealsPageHeader d-flex justify-content-between align-items-center mb-3 gap-2 flex-nowrap">
        <h2 className="mb-0">{t("sales_deals")}</h2>

        <div className="d-flex gap-2 align-items-center flex-nowrap">
          <button
            className="btn btn-outline-secondary"
            onClick={handleRefreshClick}
            disabled={loading}
          >
            {loading ? `${t("loading")}...` : t("refresh")}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {filtersError && (
        <div className="alert alert-warning py-2">{filtersError}</div>
      )}

      <div className="table-responsive dealsDesktopTable">
        <div data-academy="deals-toolbar" className="dealsDesktopToolbar d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 px-2 pt-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
              className="form-select form-select-sm"
              style={{ width: 90 }}
              value={dealsPerPage}
              onChange={handleDealsPerPageChange}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>

            <div className="text-muted">{showingText}</div>

            <label className="dealsCompletedToggle" title={t("deals_show_completed_hint")}>
              <input
                type="checkbox"
                checked={showCompletedDeals}
                onChange={(event) => {
                  setShowCompletedDeals(event.target.checked);
                  setCurrentPage(1);
                }}
              />
              <span className="dealsCompletedToggleTrack" aria-hidden="true">
                <span className="dealsCompletedToggleThumb" />
              </span>
              <span className="dealsCompletedToggleLabel">
                {t("deals_show_completed")}
              </span>
            </label>
          </div>

          <div
            className="input-group input-group-sm"
            style={{ width: "auto", minWidth: 260 }}
          >
            <select
              className="form-select"
              value={selectedFilterId || ""}
              onChange={handleSelectSavedFilter}
              disabled={filtersLoading}
            >
              <option value="">
                {filtersLoading ? `${t("loading")}...` : t("saved_filters")}
              </option>

              {savedFilters.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.is_default ? " ⭐" : ""}
                </option>
              ))}
            </select>

            <button
              className="btn btn-outline-success"
              type="button"
              title={t("save_filter")}
              onClick={handleSaveCurrentAsNew}
              disabled={savingFilter}
            >
              <i className="lni lni-floppy-disk-1"></i>
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              title={t("update_filter")}
              onClick={handleUpdateSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-refresh-circle-1-clockwise"></i>
            </button>

            <button
              className="btn btn-outline-danger"
              type="button"
              title={t("delete_filter")}
              onClick={handleDeleteSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
             <i className="lni lni-trash-3"></i>
            </button>
          </div>
        </div>

        <table data-academy="deals-table" className="table table-striped table-hover align-middle">
          <thead>
            <tr>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("ui_id")}>
                {t("id")}
                {renderSortArrow("ui_id")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("title")}>
                {t("sales_deals_title")}
                {renderSortArrow("title")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("amount")}>
                {t("sales_deals_amount")}
                {renderSortArrow("amount")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("status")}>
                {t("status")}
                {renderSortArrow("status")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("company_id")}>
                {t("sales_contacts_company")}
                {renderSortArrow("company_id")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("contact_ui_id")}>
                {t("sales_deals_contact")}
                {renderSortArrow("contact_ui_id")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("owner_ui_id")}>
                {t("sales_contacts_owner")}
                {renderSortArrow("owner_ui_id")}
              </th>

              <th data-academy="deals-pipeline-link" style={{ cursor: "pointer" }} onClick={() => handleSort("stage_id")}>
                {t("sales_deals_stage")}
                {renderSortArrow("stage_id")}
              </th>

              <th style={{ cursor: "pointer" }} onClick={() => handleSort("created_at")}>
                {t("created_at")}
                {renderSortArrow("created_at")}
              </th>

              <th>{t("tasks", { defaultValue: "Tasks" })}</th>
              <th className="dealsStickyActions" style={{ width: 90 }}>{t("actions")}</th>
            </tr>

            <tr>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("id")}
                  value={columnFilters.ui_id}
                  onChange={(e) => handleColumnFilterChange("ui_id", e.target.value)}
                />
              </th>

              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_deals_title")}
                  value={columnFilters.title}
                  onChange={(e) => handleColumnFilterChange("title", e.target.value)}
                />
              </th>

              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_deals_amount")}
                  value={columnFilters.amount}
                  onChange={(e) => handleColumnFilterChange("amount", e.target.value)}
                />
              </th>

              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("status")}
                  value={columnFilters.status}
                  onChange={(e) => handleColumnFilterChange("status", e.target.value)}
                />
              </th>

              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_contacts_company")}
                  value={columnFilters.company_id}
                  onChange={(e) => handleColumnFilterChange("company_id", e.target.value)}
                />
              </th>

              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_deals_contact")}
                  value={columnFilters.contact_ui_id}
                  onChange={(e) => handleColumnFilterChange("contact_ui_id", e.target.value)}
                />
              </th>

              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_contacts_owner")}
                  value={columnFilters.owner_ui_id}
                  onChange={(e) => handleColumnFilterChange("owner_ui_id", e.target.value)}
                />
              </th>

              <th>
                <select
                  className="form-select form-select-sm"
                  value={columnFilters.stage_id}
                  onChange={(e) => handleColumnFilterChange("stage_id", e.target.value)}
                >
                  <option value="">{t("sales_deals_stage")}</option>
                  {stages.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </th>

              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("created_at")}
                  value={columnFilters.created_at}
                  onChange={(e) => handleColumnFilterChange("created_at", e.target.value)}
                />
              </th>

              <th />
              <th className="dealsStickyActions" />
            </tr>
          </thead>

          <tbody>
            {visibleDeals.length === 0 && !loading && (
              <tr>
                <td colSpan={11} className="text-center py-4">
                  {t("sales_deals_empty")}
                </td>
              </tr>
            )}

            {visibleDeals.map((deal) => {
              const wonTitle = t("deal_mark_as_won");
              const lostTitle = t("deal_mark_as_lost");
              const taskTitle = t("create_task");

              const wonDisabled = isStatusActionDisabled(deal, "won");
              const lostDisabled = isStatusActionDisabled(deal, "lost");
              const isMenuOpen = openMenuDealId === deal.id;

              return (
                <tr key={deal.id}>
                  <td>{deal.ui_id}</td>
                  <td>{deal.title}</td>
                  <td>
                    <div className="dealAmountEditor">
                      {renderAmountInput(deal)}
                      {updatingId === deal.id ? (
                        <span
                          className="dealAmountSaving"
                          title={t("saving", { defaultValue: "Saving" })}
                          aria-label={t("saving", { defaultValue: "Saving" })}
                        >
                          …
                        </span>
                      ) : null}
                    </div>
                    {deal.coupon_code ? (
                      <div className="dealCouponMini">
                        🏷 {deal.coupon_code}
                        {deal.final_amount != null ? ` → ${deal.final_amount}` : ""}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <span className="badge bg-light text-dark">
                      {deal.status}
                    </span>
                  </td>
                  <td>{renderCompanyCell(deal)}</td>
                  <td>{renderContactCell(deal)}</td>
                  <td>{renderOwnerCell(deal)}</td>
                  <td>{getStageLabel(deal)}</td>
                  <td>
                    {deal.created_at
                      ? new Date(deal.created_at).toLocaleString()
                      : "-"}
                  </td>

                  <td>{renderTaskLinks(deal)}</td>

                  <td className="dealsStickyActions">
                    <span title={t("actions")}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={kebabBtnStyle}
                        onClick={(e) => openActionsMenu(deal.id, e)}
                        aria-expanded={isMenuOpen ? "true" : "false"}
                        aria-haspopup="menu"
                      >
                        ⋯
                      </button>
                    </span>

                    {isMenuOpen && createPortal(
                      <div
                        ref={openMenuRef}
                        role="menu"
                        aria-label={t("deals_actions_aria")}
                        style={menuStyle}
                      >
                        {getUserPermissions()?.includes("deals.update") && (
                          <MenuItem
                            title={wonTitle}
                            disabled={wonDisabled}
                            icon="✓"
                            iconBg="rgba(25,135,84,0.25)"
                            iconBorder="1px solid rgba(25,135,84,0.35)"
                            onClick={() => {
                              setOpenMenuDealId(null);
                              updateDealStatus(deal, "won");
                            }}
                          >
                            {wonTitle}
                          </MenuItem>
                        )}

                        {getUserPermissions()?.includes("deals.update") && (
                          <MenuItem
                            title={lostTitle}
                            disabled={lostDisabled}
                            icon="✕"
                            iconBg="rgba(220,53,69,0.22)"
                            iconBorder="1px solid rgba(220,53,69,0.35)"
                            onClick={() => {
                              setOpenMenuDealId(null);
                              updateDealStatus(deal, "lost");
                            }}
                          >
                            {lostTitle}
                          </MenuItem>
                        )}



                        {getUserPermissions()?.includes("deals.update") && (
                          <MenuItem
                            title={t("deal_coupon_apply", { defaultValue: "Apply coupon" })}
                            disabled={updatingId === deal.id || Boolean(deal.coupon_code)}
                            icon="🏷"
                            iconBg="rgba(255,193,7,0.22)"
                            iconBorder="1px solid rgba(255,193,7,0.35)"
                            onClick={() => applyCouponToDeal(deal)}
                          >
                            {t("deal_coupon_apply", { defaultValue: "Apply coupon" })}
                          </MenuItem>
                        )}

                        <div
                          style={{
                            height: 1,
                            margin: "6px 2px",
                            background: "rgba(255,255,255,0.10)",
                          }}
                        />

                        {getUserPermissions()?.includes("deals.update") && (
                          <button
                            type="button"
                            className="btn btn-outline-warning btn-sm"
                            onClick={() => applyCouponToDeal(deal)}
                            disabled={updatingId === deal.id || Boolean(deal.coupon_code)}
                          >
                            {t("deal_coupon_apply", { defaultValue: "Apply coupon" })}
                          </button>
                        )}

                        {getUserPermissions()?.includes("tasks.create") && (
                          <MenuItem
                            title={taskTitle}
                            disabled={false}
                            icon="+"
                            iconBg="rgba(108,117,125,0.20)"
                            iconBorder="1px solid rgba(108,117,125,0.35)"
                            onClick={() => {
                              setOpenMenuDealId(null);
                              navigate("/menu/sales/tasks", {
                                state: {
                                  deal_id: deal.id,
                                  contact_ui_ids: deal.contact_ui_id
                                    ? [deal.contact_ui_id]
                                    : [],
                                },
                              });
                            }}
                          >
                            {taskTitle}
                          </MenuItem>
                        )}

                        {getUserPermissions()?.includes("deals.delete") && (
                          <>
                            <div
                              style={{
                                height: 1,
                                margin: "6px 2px",
                                background: "rgba(255,255,255,0.10)",
                              }}
                            />

                            <MenuItem
                              title={t("delete")}
                              disabled={updatingId === deal.id}
                              icon={<i className="lni lni-trash-3"></i>}
                              iconBg="rgba(220,53,69,0.20)"
                              iconBorder="1px solid rgba(220,53,69,0.35)"
                              onClick={() => handleDeleteDeal(deal)}
                            >
                              {t("delete")}
                            </MenuItem>
                          </>
                        )}
                      </div>,
                      document.body
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="dealsMobileTableCard">
        <div className="dealsMobileTableToolbar">
          <div className="dealsMobileToolbarTitle">
            {t("table_filters", { defaultValue: "Table filters" })}
          </div>

          <div className="dealsMobileToolbarActions">
            <select
              className="form-select"
              value={selectedFilterId || ""}
              onChange={handleSelectSavedFilter}
              disabled={filtersLoading}
            >
              <option value="">
                {filtersLoading ? `${t("loading")}...` : t("saved_filters")}
              </option>

              {savedFilters.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.is_default ? " ⭐" : ""}
                </option>
              ))}
            </select>

            <button
              className="btn btn-outline-success"
              type="button"
              title={t("save_filter")}
              onClick={handleSaveCurrentAsNew}
              disabled={savingFilter}
            >
              <i className="lni lni-floppy-disk-1"></i>
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              title={t("update_filter")}
              onClick={handleUpdateSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-refresh-circle-1-clockwise"></i>
            </button>

            <button
              className="btn btn-outline-danger"
              type="button"
              title={t("delete_filter")}
              onClick={handleDeleteSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-trash-3"></i>
            </button>
          </div>
        </div>

        <div className="dealsMobileTableMeta">
          <select
            className="form-select"
            value={dealsPerPage}
            onChange={handleDealsPerPageChange}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>

          <div>{showingText}</div>

          <label className="dealsCompletedToggle dealsCompletedToggle--mobile" title={t("deals_show_completed_hint")}>
            <input
              type="checkbox"
              checked={showCompletedDeals}
              onChange={(event) => {
                setShowCompletedDeals(event.target.checked);
                setCurrentPage(1);
              }}
            />
            <span className="dealsCompletedToggleTrack" aria-hidden="true">
              <span className="dealsCompletedToggleThumb" />
            </span>
            <span className="dealsCompletedToggleLabel">
              {t("deals_show_completed")}
            </span>
          </label>
        </div>

        <div className="dealsMobileTableSearch">
          <input
            type="text"
            className="form-control"
            placeholder={t("search", { defaultValue: "Search" })}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="dealsMobileTableScroll">
          <table className="dealsMobileTable">
            <thead>
              <tr>
                <th>{t("id")}</th>
                <th>{t("sales_deals_title")}</th>
              </tr>

              <tr>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("id")}
                    value={columnFilters.ui_id}
                    onChange={(e) => handleColumnFilterChange("ui_id", e.target.value)}
                  />
                </th>

                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("sales_deals_title")}
                    value={columnFilters.title}
                    onChange={(e) => handleColumnFilterChange("title", e.target.value)}
                  />
                </th>
              </tr>
            </thead>

            <tbody>
              {visibleDeals.length === 0 && !loading && (
                <tr>
                  <td colSpan={2} className="dealsMobileEmptyCell">
                    {t("sales_deals_empty")}
                  </td>
                </tr>
              )}

              {visibleDeals.map((deal) => (
                <tr key={deal.id}>
                  <td className="dealsMobileIdCell">{deal.ui_id}</td>

                  <td>
                    <div className="dealsMobileDealCell">
                      <div className="dealsMobileDealTop">
                        <strong>{deal.title}</strong>

                        <span className={`dealsMobileStatus dealsMobileStatus-${deal.status || "unknown"}`}>
                          {deal.status}
                        </span>
                      </div>

                      <div className="dealsMobileDealMeta">
                        <span>
                          <span className="dealMobileAmountField">
                            <span>{t("sales_deals_amount")}:</span>
                            {renderAmountInput(deal, true)}
                          </span>{" "}
                          / {t("deal_prepayment", { defaultValue: "Prepayment" })}: <b>{deal.prepayment ?? "-"}</b> / {t("deal_balance_due", { defaultValue: "Balance due" })}: <b>{deal.balance ?? "-"}</b>
                          {deal.coupon_code ? (
                            <span className="dealCouponMini"> 🏷 {deal.coupon_code}{deal.final_amount != null ? ` → ${deal.final_amount}` : ""}</span>
                          ) : null}
                        </span>
                        <MobileRelationLine label={t("sales_contacts_company")}>
                          {renderCompanyCell(deal)}
                        </MobileRelationLine>
                        <MobileRelationLine label={t("sales_deals_contact")}>
                          {renderContactCell(deal)}
                        </MobileRelationLine>
                        <MobileRelationLine label={t("sales_contacts_owner")}>
                          {renderOwnerCell(deal)}
                        </MobileRelationLine>
                        <MobileRelationLine label={t("sales_deals_stage")}>
                          <span className="crmMobilePlainValue">{getStageLabel(deal)}</span>
                        </MobileRelationLine>
                      </div>

                      <div className="dealsMobileDealDate">
                        {deal.created_at ? new Date(deal.created_at).toLocaleString() : "-"}
                      </div>

                      <div className="dealsMobileDealActions">
                        {getUserPermissions()?.includes("deals.update") && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={() => updateDealStatus(deal, "won")}
                            disabled={isStatusActionDisabled(deal, "won")}
                          >
                            {t("deal_mark_as_won")}
                          </button>
                        )}

                        {getUserPermissions()?.includes("deals.update") && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => updateDealStatus(deal, "lost")}
                            disabled={isStatusActionDisabled(deal, "lost")}
                          >
                            {t("deal_mark_as_lost")}
                          </button>
                        )}

                        {getUserPermissions()?.includes("tasks.create") && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm dealsMobileTaskBtn"
                            onClick={() =>
                              navigate("/menu/sales/tasks", {
                                state: {
                                  deal_id: deal.id,
                                  contact_ui_ids: deal.contact_ui_id
                                    ? [deal.contact_ui_id]
                                    : [],
                                },
                              })
                            }
                          >
                            {t("create_task")}
                          </button>
                        )}

                        {getUserPermissions()?.includes("deals.delete") && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm dealsMobileDeleteBtn"
                            onClick={() => handleDeleteDeal(deal)}
                            disabled={updatingId === deal.id}
                          >
                            <i className="lni lni-trash-3"></i>
                            <span>{t("delete")}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2 dealsPaginationWrap">
        <nav aria-label={t("deals_pagination_aria")}>
          <ul className="pagination justify-content-end mb-0 dealsPagination dealsCompactPagination">
            <li className={`page-item ${safePage === 1 || totalDeals === 0 ? "disabled" : ""}`}><button className="page-link" title={t("first", { defaultValue: "First" })} aria-label={t("first", { defaultValue: "First" })} onClick={() => paginate(1)}>«</button></li>
            <li
              className={`page-item ${
                safePage === 1 || totalDeals === 0 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                title={t("previous")}
                aria-label={t("previous")}
                onClick={() => paginate(safePage - 1)}
              >
                {t("previous")}
              </button>
            </li>

            {getCompactPaginationItems(safePage, totalPages).map((item) => {
              if (typeof item === "string") {
                return (
                  <li key={item} className="page-item disabled dealsPaginationEllipsis">
                    <span className="page-link">…</span>
                  </li>
                );
              }
              return (
                <li key={item} className={`page-item ${safePage === item ? "active" : ""}`}>
                  <button className="page-link" onClick={() => paginate(item)}>
                    {item}
                  </button>
                </li>
              );
            })}

            <li
              className={`page-item ${
                safePage === totalPages || totalDeals === 0 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                title={t("next")}
                aria-label={t("next")}
                onClick={() => paginate(safePage + 1)}
              >
                {t("next")}
              </button>
            </li>
            <li className={`page-item ${safePage === totalPages || totalDeals === 0 ? "disabled" : ""}`}><button className="page-link" title={t("last", { defaultValue: "Last" })} aria-label={t("last", { defaultValue: "Last" })} onClick={() => paginate(totalPages)}>»</button></li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default DealsPage;
